import { motion } from 'framer-motion';
import { Server, Globe, RefreshCw, Headphones } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const content = {
  nl: {
    eyebrow: 'Hosting & Support',
    title: 'Ook na de oplevering blijven we je ondersteunen',
    subtitle: 'Naast de eenmalige opstartkost kun je kiezen voor een maandelijkse bijdrage voor hosting, domein en ondersteuning. Zo blijft jouw website veilig, snel en technisch in orde.',
    services: [
      {
        icon: Server,
        title: 'Hosting & SSL',
        description: 'Snelle en veilige hosting met SSL-beveiliging voor een betrouwbare website.'
      },
      {
        icon: Globe,
        title: 'Domeinnaam',
        description: 'Registratie en verlenging van je domeinnaam, zoals .be, .nl of .com.'
      },
      {
        icon: RefreshCw,
        title: 'Updates & Back-ups',
        description: 'Technische opvolging, back-ups en kleine inhoudelijke wijzigingen.'
      },
      {
        icon: Headphones,
        title: 'Ondersteuning',
        description: 'Snelle hulp bij vragen via e-mail, WhatsApp of rechtstreeks via de chatbot.'
      }
    ]
  },
  en: {
    eyebrow: 'Hosting & Support',
    title: 'We continue to support you after delivery',
    subtitle: 'In addition to the one-time startup cost, you can choose a monthly contribution for hosting, domain and support. This keeps your website secure, fast and technically in order.',
    services: [
      {
        icon: Server,
        title: 'Hosting & SSL',
        description: 'Fast and secure hosting with SSL security for a reliable website.'
      },
      {
        icon: Globe,
        title: 'Domain name',
        description: 'Registration and renewal of your domain name, such as .be, .nl or .com.'
      },
      {
        icon: RefreshCw,
        title: 'Updates & Backups',
        description: 'Technical monitoring, backups and small content changes.'
      },
      {
        icon: Headphones,
        title: 'Support',
        description: 'Quick help with questions via email, WhatsApp or directly via the chatbot.'
      }
    ]
  },
  fr: {
    eyebrow: 'Hébergement & Support',
    title: 'Nous continuons à vous soutenir après la livraison',
    subtitle: 'En plus du coût de démarrage, vous pouvez choisir une contribution mensuelle pour l\'hébergement, le domaine et le support.',
    services: [
      { icon: Server, title: 'Hébergement & SSL', description: 'Hébergement rapide et sécurisé avec sécurité SSL.' },
      { icon: Globe, title: 'Nom de domaine', description: 'Enregistrement et renouvellement de votre nom de domaine.' },
      { icon: RefreshCw, title: 'Mises à jour & Sauvegardes', description: 'Suivi technique, sauvegardes et petites modifications.' },
      { icon: Headphones, title: 'Support', description: 'Aide rapide via e-mail, WhatsApp ou chatbot.' }
    ]
  },
  de: {
    eyebrow: 'Hosting & Support',
    title: 'Wir unterstützen Sie auch nach der Lieferung',
    subtitle: 'Zusätzlich zu den einmaligen Startkosten können Sie einen monatlichen Beitrag für Hosting, Domain und Support wählen.',
    services: [
      { icon: Server, title: 'Hosting & SSL', description: 'Schnelles und sicheres Hosting mit SSL-Sicherheit.' },
      { icon: Globe, title: 'Domainname', description: 'Registrierung und Verlängerung Ihres Domainnamens.' },
      { icon: RefreshCw, title: 'Updates & Backups', description: 'Technische Überwachung, Backups und kleine Änderungen.' },
      { icon: Headphones, title: 'Support', description: 'Schnelle Hilfe per E-Mail, WhatsApp oder Chatbot.' }
    ]
  }
};

export const SocialProofSection = () => {
  const { language } = useLanguage();
  const t = content[language] || content.nl;

  return (
    <section 
      className="py-20 sm:py-28 bg-[#F3F4F6]"
      data-testid="hosting-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <span 
            className="inline-block px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4 bg-blue-100 text-[#2563EB]"
          >
            {t.eyebrow}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-[#111827] mb-6 leading-tight">
            {t.title}
          </h2>
          <p className="text-lg text-[#4B5563] leading-relaxed">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-blue-100">
                  <Icon size={24} className="text-[#2563EB]" />
                </div>
                <h3 className="text-lg font-bold text-[#111827] mb-2">{service.title}</h3>
                <p className="text-[#4B5563] text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
